import superAdminApi from "./superAdminApi";
import CommonService from "./common.service";

const isSupportManagerPortal = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/dashboard/support-manager");

const isLogisticsManagerPortal = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/dashboard/logistics-manager");

const getManagerAccessBase = () => {
  if (isLogisticsManagerPortal()) return "/logistics-manager/access";
  if (isSupportManagerPortal()) return "/support-manager/access";
  return null;
};

export const accessExtractMessage = (err, fallback = "Request failed") =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

export const fetchAccessPermissionCatalog = async () => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.get(`${managerBase}/permissions/catalog`, {}, { silent: true });
    return res?.data ?? { groups: [], actionLabels: {}, permissions: [], totalPermissions: 0 };
  }
  const res = await superAdminApi.get("/super-admin/access/permissions/catalog");
  return res.data?.data ?? { groups: [], actionLabels: {}, permissions: [], totalPermissions: 0 };
};

export const fetchAccessRoles = async (params = {}) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.get(`${managerBase}/roles`, params, { silent: true });
    return res?.data ?? { items: [], total: 0 };
  }
  const res = await superAdminApi.get("/super-admin/access/roles", { params });
  return res.data?.data ?? { items: [], total: 0 };
};

export const fetchAccessRoleById = async (id) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.get(`${managerBase}/roles/${id}`, {}, { silent: true });
    return res?.data?.role ?? null;
  }
  const res = await superAdminApi.get(`/super-admin/access/roles/${id}`);
  return res.data?.data?.role ?? null;
};

export const createAccessRole = async (payload) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.post(`${managerBase}/roles`, payload, { silent: true });
    return res?.data?.role ?? null;
  }
  const res = await superAdminApi.post("/super-admin/access/roles", payload);
  return res.data?.data?.role ?? null;
};

export const updateAccessRole = async (id, payload) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.put(`${managerBase}/roles/${id}`, payload, { silent: true });
    return res?.data?.role ?? null;
  }
  const res = await superAdminApi.put(`/super-admin/access/roles/${id}`, payload);
  return res.data?.data?.role ?? null;
};

export const deleteAccessRole = async (id) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.delete(`${managerBase}/roles/${id}`, { silent: true });
    return res?.data ?? null;
  }
  const res = await superAdminApi.delete(`/super-admin/access/roles/${id}`);
  return res.data?.data ?? null;
};

export const fetchAccessUsers = async (params = {}) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.get(`${managerBase}/users`, params, { silent: true });
    return res?.data ?? { items: [], total: 0 };
  }
  const res = await superAdminApi.get("/super-admin/access/users", { params });
  return res.data?.data ?? { items: [], total: 0 };
};

export const fetchAccessUserById = async (id) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.get(`${managerBase}/users/${id}`, {}, { silent: true });
    return res?.data?.user ?? null;
  }
  const res = await superAdminApi.get(`/super-admin/access/users/${id}`);
  return res.data?.data?.user ?? null;
};

export const createAccessUser = async (payload) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.post(`${managerBase}/users`, payload, { silent: true });
    return res?.data?.user ?? null;
  }
  const res = await superAdminApi.post("/super-admin/access/users", payload);
  return res.data?.data?.user ?? null;
};

export const updateAccessUser = async (id, payload) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.put(`${managerBase}/users/${id}`, payload, { silent: true });
    return res?.data?.user ?? null;
  }
  const res = await superAdminApi.put(`/super-admin/access/users/${id}`, payload);
  return res.data?.data?.user ?? null;
};

export const deleteAccessUser = async (id) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.delete(`${managerBase}/users/${id}`, { silent: true });
    return res?.data ?? null;
  }
  const res = await superAdminApi.delete(`/super-admin/access/users/${id}`);
  return res.data?.data ?? null;
};

export const resendAccessUserInvitation = async (id) => {
  const managerBase = getManagerAccessBase();
  if (managerBase) {
    const res = await CommonService.post(
      `${managerBase}/users/${id}/resend-invitation`,
      {},
      { silent: true },
    );
    return res?.data?.user ?? null;
  }
  const res = await superAdminApi.post(`/super-admin/access/users/${id}/resend-invitation`);
  return res.data?.data?.user ?? null;
};

export const resetAccessUserPassword = async (id) => {
  const managerBase = getManagerAccessBase();
  if (!managerBase) {
    throw new Error("Password resets for team users are managed by their manager.");
  }
  const res = await CommonService.post(
    `${managerBase}/users/${id}/reset-password`,
    {},
    { silent: true },
  );
  return res?.data?.user ?? null;
};

export const updateAccessUserAvailability = async (id, availability) => {
  if (!isSupportManagerPortal()) {
    throw new Error("Support User availability is managed by their Support Manager.");
  }
  const res = await CommonService.patch(
    `/support-manager/access/users/${id}/availability`,
    { availability },
    { silent: true },
  );
  return res?.data?.user ?? null;
};
